import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'

import type { GroupMember } from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { ActivateGroupAction } from '@/features/groups/activate-group'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { GroupHealthPanel } from '@/features/groups/health-panel'
import { HealthIndicator, RoundEligibility } from '@/features/groups/health'
import { describeHealth } from '@/features/groups/health-presentation'
import {
  GroupMeetingsPanel,
  GroupMembersPanel,
  GroupOverviewPanel,
} from '@/features/groups/group-panels'
import {
  AddMemberDialog,
  AssignOfficerDialog,
  ChangeLeaderDialog,
  ExitMemberDialog,
  MembershipActions,
} from '@/features/groups/membership-actions'
import { useGroupPermissions } from '@/features/groups/permissions'
import { useGroup, useGroupHealth } from '@/features/groups/queries'
import {
  MeetingLocationDialog,
  ScheduleActions,
  ScheduleMeetingsDialog,
} from '@/features/groups/schedule-actions'

import type { GroupDetailTab } from './search'

const PANEL_ID = 'group-detail-panel'

type OpenDialog = 'addMember' | 'exitMember' | 'changeLeader' | 'assignOfficer' | 'location' | 'schedule' | null

/**
 * The group record.
 *
 * The health summary sits directly under the header, above the tabs, in the same place and for the
 * same reason the exposure banner does on a customer: it is the finding a decision turns on, and
 * it only works if it is read before the decision rather than found afterwards. The tab that
 * explains it is one click away and is where the argument is actually made — but nobody should
 * have to go looking to learn that a group cannot be scored.
 *
 * Members read on a phone by policy, so nothing here refuses a narrow screen. The tables become
 * cards at `lg`, the actions wrap, and the dialogs are full-width.
 */
export function GroupDetailPage() {
  const { groupId } = useParams({ from: '/authenticated/groups/$groupId' })
  const search = useSearch({ from: '/authenticated/groups/$groupId' })
  const navigate = useNavigate()
  const permissions = useGroupPermissions()

  const [dialog, setDialog] = useState<OpenDialog>(null)
  const [exiting, setExiting] = useState<GroupMember | null>(null)

  const tab = search.tab ?? 'overview'

  const query = useGroup(groupId)

  /**
   * The health tab reads the stored snapshot from its own endpoint rather than the copy embedded
   * in the group. The two are the same figure until somebody asks for a recompute, and after that
   * the endpoint is the one that is current — which is exactly the moment this panel is being
   * looked at.
   */
  const health = useGroupHealth(groupId)

  const selectTab = (next: GroupDetailTab) => {
    void navigate({
      to: '/groups/$groupId',
      params: { groupId },
      search: { tab: next },
      // Moving between tabs is not a place someone means to come back to with the Back button.
      replace: true,
    })
  }

  const closeDialog = () => setDialog(null)

  return (
    <>
      <Link
        to="/groups"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All groups
      </Link>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-9 w-full max-w-xl" />
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This group could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={query.data.name}
            reference={query.data.groupCode}
            actions={
              permissions.canUpdate ? <ActivateGroupAction group={query.data} /> : undefined
            }
            badges={
              <>
                <StatusBadge status={query.data.status} />
                <HealthIndicator health={describeHealth(query.data)} />
              </>
            }
          />

          {/*
            The one-line answer to "can this group take another round", before any tab is chosen.
            An unscored group says why here rather than reading as a quiet no.
          */}
          <Alert
            tone={
              query.data.healthQualifies
                ? 'success'
                : query.data.healthOutcome === null || !describeHealth(query.data).isScored
                  ? 'info'
                  : 'danger'
            }
          >
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
              <RoundEligibility health={describeHealth(query.data)} />
              <div className="min-w-0 flex-1 space-y-0.5">
                <AlertTitle>{describeHealth(query.data).outcomeLabel}</AlertTitle>
                <AlertDescription>{describeHealth(query.data).explanation}</AlertDescription>
                {permissions.canViewHealth ? (
                  // Its own line rather than a link inside the sentence: inline text is a 20px
                  // target, and this is the control somebody reaches for on a phone precisely
                  // because they have just been told the group cannot take another round.
                  <button
                    type="button"
                    onClick={() => selectTab('health')}
                    className="inline-flex items-center text-sm font-medium text-brand-text underline underline-offset-2 hover:text-brand-text-hover max-lg:min-h-11"
                  >
                    See the inputs
                  </button>
                ) : null}
              </div>
            </div>
          </Alert>

          <div className="flex flex-wrap items-center gap-2">
            <MembershipActions
              group={query.data}
              onAddMember={() => setDialog('addMember')}
              onChangeLeader={() => setDialog('changeLeader')}
              onAssignOfficer={() => setDialog('assignOfficer')}
            />
            <ScheduleActions
              group={query.data}
              onSetLocation={() => setDialog('location')}
              onSchedule={() => setDialog('schedule')}
            />
          </div>

          <div className="space-y-4">
            <TabStrip<GroupDetailTab>
              tabs={
                [
                  { id: 'overview', label: 'Overview' },
                  { id: 'members', label: 'Members', badge: query.data.members.length },
                  { id: 'meetings', label: 'Meetings' },
                  { id: 'health', label: 'Health' },
                ] satisfies TabDefinition<GroupDetailTab>[]
              }
              active={tab}
              onChange={selectTab}
              panelId={PANEL_ID}
              label="Group record sections"
            />

            <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
              {tab === 'overview' ? <GroupOverviewPanel group={query.data} /> : null}

              {tab === 'members' ? (
                <GroupMembersPanel
                  group={query.data}
                  onExitMember={(member) => {
                    setExiting(member)
                    setDialog('exitMember')
                  }}
                />
              ) : null}

              {tab === 'meetings' ? <GroupMeetingsPanel group={query.data} /> : null}

              {tab === 'health' ? (
                !permissions.canViewHealth ? (
                  <Alert tone="warning">
                    <AlertTitle>The health assessment is restricted</AlertTitle>
                    <AlertDescription>
                      Reading a group&apos;s score needs the groups.health.view permission. The
                      summary above is what the group list carries for every reader.
                    </AlertDescription>
                  </Alert>
                ) : health.isPending ? (
                  <Skeleton className="h-64 w-full" />
                ) : health.isError ? (
                  <ErrorSurface
                    error={health.error}
                    onRetry={() => void health.refetch()}
                    title="The health assessment could not be read"
                  />
                ) : (
                  <GroupHealthPanel groupId={groupId} health={health.data} />
                )
              ) : null}
            </div>
          </div>

          <AddMemberDialog
            group={query.data}
            open={dialog === 'addMember'}
            onOpenChange={(open) => {
              if (!open) {
                closeDialog()
              }
            }}
          />

          <ExitMemberDialog
            group={query.data}
            member={exiting}
            open={dialog === 'exitMember'}
            onOpenChange={(open) => {
              if (!open) {
                closeDialog()
              }
            }}
          />

          <ChangeLeaderDialog
            group={query.data}
            open={dialog === 'changeLeader'}
            onOpenChange={(open) => {
              if (!open) {
                closeDialog()
              }
            }}
          />

          <AssignOfficerDialog
            group={query.data}
            open={dialog === 'assignOfficer'}
            onOpenChange={(open) => {
              if (!open) {
                closeDialog()
              }
            }}
          />

          <MeetingLocationDialog
            group={query.data}
            open={dialog === 'location'}
            onOpenChange={(open) => {
              if (!open) {
                closeDialog()
              }
            }}
          />

          <ScheduleMeetingsDialog
            group={query.data}
            open={dialog === 'schedule'}
            onOpenChange={(open) => {
              if (!open) {
                closeDialog()
              }
            }}
          />
        </>
      )}
    </>
  )
}
